import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import FeedbackModal from 'components/FeedbackModal/FeedbackModal';
import { useSubmitFeedbackMutation } from 'store/services/feedback/feedbackApi';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import './NotFound404.css';

const NotFound404: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [submitFeedback, { isLoading: isSubmittingFeedback }] = useSubmitFeedbackMutation();

  // Set page title
  useEffect(() => {
    document.title = '404 - Page Not Found | TradeMagic.gg';
  }, []);

  // Vindicate's scryfall ID (Eternal Masters printing)
  const vindicateScryfallId = '9f969be3-ca00-4caf-89f8-2b520e15ad6a';
  const backgroundImage = AssetUrls.cards.getCardImage(vindicateScryfallId, 'art_crop');

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search/results?name=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleGoHome = () => {
    navigate('/home');
  };

  const handleFeedbackSubmit = async (feedbackData: any) => {
    try {
      const result = await submitFeedback(feedbackData).unwrap();
      if (result.success) {
        toast.success('Thank you for your feedback!');
        setIsFeedbackModalOpen(false);
      } else {
        toast.error(result.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <div className="not-found-container">
      {/* Blurred Background */}
      <div
        className="not-found-bg"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(110px)'
        }}
      />

      {/* Content */}
      <div className="not-found-content">
        {/* Mana Symbols: 4-0-4 */}
        <div className="not-found-mana-symbols">
          <img
            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/4.svg"
            alt="Four mana"
            className="mana-symbol"
          />
          <img
            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/0.svg"
            alt="Zero mana"
            className="mana-symbol"
          />
          <img
            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/4.svg"
            alt="Four mana"
            className="mana-symbol"
          />
        </div>

        {/* Message */}
        <h1 className="not-found-title">Looks like this page was exiled from the battlefield.</h1>
        <p className="not-found-subtitle">It doesn't exist or it was moved to another zone.</p>

        {/* Search Bar */}
        <div className="not-found-search">
          <input
            type="text"
            className="not-found-search-input"
            placeholder="Search for cards"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button className="not-found-search-button" onClick={handleSearch}>
            <img
              src={AssetUrls.icons.navigation.search}
              alt="Search"
            />
            Go
          </button>
        </div>

        {/* Go Home Button */}
        <div className="not-found-home-button">
          <MagicButton
            label="Go Home"
            icon={<img src={AssetUrls.icons.navigation.adminHome} alt="" style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }} />}
            color="purple"
            onPress={handleGoHome}
            fullWidth={false}
          />
        </div>

        {/* Feedback Link */}
        <div className="not-found-feedback">
          or send us some{' '}
          <button
            className="not-found-feedback-link"
            onClick={() => setIsFeedbackModalOpen(true)}
          >
            feedback
          </button>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        isLoading={isSubmittingFeedback}
      />
    </div>
  );
};

export default NotFound404;
